import React, { useState } from 'react';
import { Image, ImageStyle, StyleProp } from 'react-native';
import { thumbUrlFor } from '../../utils/recipeImageVariant';

type ResizeMode = 'cover' | 'contain' | 'stretch' | 'repeat' | 'center';

interface RemoteImageProps {
  /** Image URL. Absent, empty or failed all render `fallback`. */
  uri?: string | null;
  style?: StyleProp<ImageStyle>;
  resizeMode?: ResizeMode;
  /** Drawn whenever there is no usable photo. Each surface supplies its own. */
  fallback: React.ReactNode;
  /**
   * `thumb` asks for the small stored variant. Only affects blob-hosted recipe
   * photos; everything else ignores it. Use on anything painting into a box
   * smaller than ~180pt — never on the detail hero.
   */
  variant?: 'full' | 'thumb';
  testID?: string;
  accessibilityLabel?: string;
}

/**
 * A remote image that degrades to `fallback` when the URL is missing *or* fails
 * to load.
 *
 * Most call sites used to guard only the missing-URL case
 * (`recipe.image ? <Image/> : <Placeholder/>`), which says nothing about a URL
 * that resolves to a 403 or a timeout — those rendered an empty box. A blocked
 * Vercel Blob store made that visible across the whole app at once, but a single
 * dead URL has always produced the same hole.
 *
 * Failures are tracked as the *set of URLs that failed* rather than a boolean,
 * so a recycled `FlatList` cell that once failed shows its new photo instead of
 * being stuck on the fallback forever.
 *
 * With `variant="thumb"` the source degrades in two steps — thumbnail, then
 * full-size, then `fallback`. The middle step is load-bearing: thumbnails are
 * backfilled over an existing corpus, so a recipe whose variant has not been
 * generated yet (or failed to generate) must still show its photo rather than a
 * placeholder. A missing thumbnail is a performance miss, never a visual one.
 */
export const RemoteImage: React.FC<RemoteImageProps> = ({
  uri,
  style,
  resizeMode = 'cover',
  fallback,
  variant = 'full',
  testID,
  accessibilityLabel,
}) => {
  const [failed, setFailed] = useState<readonly string[]>([]);

  const thumb = variant === 'thumb' ? thumbUrlFor(uri) : null;
  // Candidates in preference order; `thumb` is null unless asked for and derivable.
  const candidates = (thumb && thumb !== uri ? [thumb, uri] : [uri]).filter(
    (candidate): candidate is string => !!candidate
  );

  const source = candidates.find((candidate) => !failed.includes(candidate));

  if (!source) {
    return <>{fallback}</>;
  }

  return (
    <Image
      // Remount on source change so RN Web re-runs the load for the next
      // candidate instead of reusing the errored node.
      key={source}
      source={{ uri: source }}
      style={style}
      resizeMode={resizeMode}
      onError={() => setFailed((prev) => (prev.includes(source) ? prev : [...prev, source]))}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
    />
  );
};

export default RemoteImage;
